import React from "react";
import { Building2, Users, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTransactionalEmail } from "@/hooks/use-transactional-email";

interface FacilitiesTabProps {
  nursingHome: {
    facilities?: string[];
    services?: string[];
    amenities?: string[];
    room_types?: string[];
    email?: string;
    center_name?: string;
    county_slug?: string;
    nh_slug?: string;
    county_name?: string;
  };
}

export function FacilitiesTab({ nursingHome }: FacilitiesTabProps) {
  const isEmpty =
    (!nursingHome.facilities || nursingHome.facilities.length === 0) &&
    (!nursingHome.services || nursingHome.services.length === 0) &&
    (!nursingHome.amenities || nursingHome.amenities.length === 0);

  const { sendEmail } = useTransactionalEmail();
  const [emailSent, setEmailSent] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const handleRequestInfo = async () => {
    if (!nursingHome.email) return;
    setLoading(true);
    try {
      await sendEmail({
        type: 'info_request',
        data: {
          email: nursingHome.email,
          nursingHomeName: nursingHome.center_name,
          claimLink: `${window.location.origin}/nursing-homes/${nursingHome.county_slug}/${nursingHome.nh_slug}?action=claim`,
          county: nursingHome.county_name || '',
        }
      });
      setEmailSent(true);
    } catch (e) {
      // Optionally handle error
    } finally {
      setLoading(false);
    }
  };

  if (isEmpty) {
    return (
      <div className="flex flex-col items-center justify-center py-16 w-full">
        <img
          src="/lovable-uploads/47f2aef4-6ba9-49f0-a0ee-dd0d0dc05675.png"
          alt="Friendly illustration of people talking in a care home"
          className="mb-6 w-full max-w-md"
        />
        <p className="text-lg text-gray-600 mb-4 text-center">
          This nursing home hasn't shared details about their facilities, services, or amenities yet.
        </p>
        <Button onClick={handleRequestInfo} disabled={emailSent || loading}>
          {emailSent ? "Thanks, email sent" : loading ? "Sending..." : "Ask this nursing home to share more information"}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {nursingHome.facilities && nursingHome.facilities.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-grandpal-beige p-6">
          <h2 className="text-2xl font-semibold mb-4">Facilities</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {nursingHome.facilities.map((facility, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="bg-grandpal-beige rounded-full p-2 mt-1">
                  <Building2 className="h-4 w-4 text-grandpal-green" />
                </div>
                <div>
                  <p>{facility}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {nursingHome.services && nursingHome.services.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-grandpal-beige p-6">
          <h2 className="text-2xl font-semibold mb-4">Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {nursingHome.services.map((service, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="bg-grandpal-beige rounded-full p-2 mt-1">
                  <Users className="h-4 w-4 text-grandpal-green" />
                </div>
                <div>
                  <p>{service}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {nursingHome.amenities && nursingHome.amenities.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-grandpal-beige p-6">
          <h2 className="text-2xl font-semibold mb-4">Amenities</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {nursingHome.amenities.map((amenity, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="bg-grandpal-beige rounded-full p-2 mt-1">
                  <Star className="h-4 w-4 text-grandpal-green" />
                </div>
                <div>
                  <p>{amenity}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {nursingHome.room_types && nursingHome.room_types.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-grandpal-beige p-6">
          <h2 className="text-2xl font-semibold mb-4">Room Types</h2>
          <div className="flex flex-wrap gap-4">
            {nursingHome.room_types.map((roomType, index) => (
              <div key={index} className="bg-grandpal-beige/50 rounded-full px-4 py-2 text-sm">
                {roomType}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
